import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from "react";
import { VideoJsPlayerOptions } from "video.js";
import {
  PlayerAction,
  PlayerState,
  initialPlayerState
} from "../../../../../components/app-video/video-models";
import { HomeItem, UGCList } from "../../../../../models/home-rows";
import AppContext from "../../../../../services/app-context-services";
import { assignStreamLinkParams } from "../../../../../services/player-service";
import useWindowWidth from "../../../../../services/window-width-hook";
import UgcModal from "../../../../shared/ugcModal/ugcModal.component";
import ImagePlayer from "../components/ImagePlayer/ImagePlayer.component";
import UgcPlayer from "../components/Player/UgcPlayer.component";
import UgcPlayerControllerDesktop from "../controller/Desktop/UgcPlayerControllerDesktop.component";
import UgcPlayerControllerMobile from "../controller/Mobile/UgcPlayerControllerMobile.component";
import UgcPlayerController from "../controller/UgcPlayerController.component";
import { GetUgcFileType } from "../helpers";
import { useFetchNextPage } from "../hooks/useFetchNextPage";
import { FileType } from "../models/funUgc.model";
import styles from "./funUGCModal.module.scss";

interface FunUGCModalProps {
  ugcIndex: number;
  homeItem: HomeItem;
  setHomeItem: React.Dispatch<React.SetStateAction<HomeItem>>;
  setShowUgcModal: Dispatch<SetStateAction<boolean>>;
}

// const TOP_TITLE = "دیجیتونی‌ها ساختن";

export type ImagePlayerAction = "Loading" | "Play" | "Pause";

const FunUGCModal = ({
  ugcIndex,
  homeItem,
  setHomeItem,
  setShowUgcModal
}: FunUGCModalProps) => {
  const [playerState, setPlayerState] =
    useState<PlayerState>(initialPlayerState);
  const [action, setAction] = useState<PlayerAction>();
  const [fileType, setFileType] = useState<FileType>();
  const [imageState, setImageState] = useState<ImagePlayerAction>("Loading");

  const [mainUgcIndex, setMainUgcIndex] = useState(ugcIndex);
  const [mainUgc, setMainUgc] = useState<UGCList>();
  const [ugcSource, setUgcSource] = useState("");
  const { isMobile } = useWindowWidth();
  const { showLoginPopup, dg_id } = useContext(AppContext);

  const { fetchNextPage, isLoading, output } = useFetchNextPage(homeItem);

  const UgcIndexSetter = async (
    ugcIndex: number,
    operation: "next" | "previous"
  ) => {
    if (
      operation === "next" &&
      ugcIndex + 1 === Math.floor(homeItem.ugc_list.length - 1)
    ) {
      if (isLoading) return;

      await fetchNextPage();
      // console.log("AFTER FETCH");
    }

    if (operation === "next" && ugcIndex + 1 < homeItem.ugc_list.length) {
      // console.log("NEXT");

      setMainUgcIndex(s => (s = ugcIndex + 1));
      await GetUgcFileType(mainUgc[ugcIndex + 1]).then(data => {
        setFileType(data);
      });
    } else if (operation === "previous" && ugcIndex - 1 >= 0) {
      setMainUgcIndex(s => (s = ugcIndex - 1));

      await GetUgcFileType(mainUgc[ugcIndex - 1]).then(data => {
        setFileType(data);
      });
    } else if (operation === "previous" && ugcIndex - 1 < 0) {
      setMainUgcIndex(ugcIndex);
      setShowUgcModal(false);
    } else {
      setShowUgcModal(false);
    }
  };

  const ReachEndHandler = async () => {
    await UgcIndexSetter(mainUgcIndex, "next");
  };

  useEffect(() => {
    setHomeItem(output);
    // console.log("CHANGE OUTPUT");
  }, [output]);

  useEffect(() => {
    setMainUgc(homeItem.ugc_list[mainUgcIndex]);
    // console.log("CHANGE MAIN UGC");
  }, [homeItem, mainUgcIndex]);

  useEffect(() => {
    if (!mainUgc) return;
    GetUgcFileType(mainUgc)
      .then(data => {
        setFileType(data);
        // console.log({file: mainUgc.file});
        const trimedUrl = assignStreamLinkParams(mainUgc.file, {
          campaign: "N-A",
          trace_id: dg_id,
          source: "digitoon",
          platform: "web"
        });
        // console.log({trimedUrl});

        setUgcSource(trimedUrl);
      })
      .catch(e => {
        console.log("ERROR : ", e);
      });
  }, [mainUgc]);

  useEffect(() => {
    if (showLoginPopup) {
      setAction({ action: "pause" });
    } else if (!showLoginPopup) {
      setAction({ action: "play" });
    }
  }, [showLoginPopup]);

  const getVideoJsOptions = (): VideoJsPlayerOptions => {
    const options: VideoJsPlayerOptions = {
      muted: false,
      autoplay: true,
      normalizeAutoplay: true,
      controls: false,
      aspectRatio: "800:400",
      preload: "metadata",
      bigPlayButton: true,
      responsive: true,
      retryOnError: true,

      sources: [
        {
          src: ugcSource || "",
          type: "application/x-mpegURL"
        }
      ]
    };

    return options;
  };

  return (
    <>
      <UgcModal
        hasCloseBtn={true}
        modalStyle={{ margin: 0, padding: 0 }}
        bodyStyle={{ margin: 0, padding: 0 }}
        title={homeItem.title}
        onClose={() => setShowUgcModal(false)}
        onNext={async () => await UgcIndexSetter(mainUgcIndex, "next")}
        onPrevious={async () => await UgcIndexSetter(mainUgcIndex, "previous")}
      >
        <div className={styles.mainWrapper}>
          {fileType &&
            (fileType === "Dub" ||
              fileType === "Story" ||
              fileType === "Gallery" ||
              fileType === "Dubsmash") && (
              <div className={styles.playerWrapper}>
                {ugcSource && (
                  <UgcPlayer
                    ugcList={mainUgc}
                    isPlayerPage={true}
                    options={getVideoJsOptions()}
                    onState={setPlayerState}
                    action={action}
                    state={playerState}
                    onEndHandler={() => ReachEndHandler()}
                    customControls={
                      <UgcPlayerController
                        state={playerState}
                        setAction={setAction}
                        fileType={fileType}
                        setImageState={setImageState}
                        onNext={async () =>
                          await UgcIndexSetter(mainUgcIndex, "next")
                        }
                        onPrevious={async () =>
                          await UgcIndexSetter(mainUgcIndex, "previous")
                        }
                      />
                    }
                  />
                )}
              </div>
            )}

          {fileType && (fileType === "Painting" || fileType === "Coloring") && (
            <div className={styles.imageFrame}>
              <div className={styles.imageWrapper}>
                <ImagePlayer
                  imageSrc={mainUgc.file}
                  imageState={imageState}
                  setImageState={setImageState}
                  customControls={
                    <UgcPlayerController
                      state={playerState}
                      setAction={setAction}
                      fileType={fileType}
                      setImageState={setImageState}
                      onNext={async () =>
                        await UgcIndexSetter(mainUgcIndex, "next")
                      }
                      onPrevious={async () =>
                        await UgcIndexSetter(mainUgcIndex, "previous")
                      }
                    />
                  }
                />
              </div>
            </div>
          )}
        </div>

        {!isMobile && (
          <div className={styles.desktopController}>
            <UgcPlayerControllerDesktop
              ugc={mainUgc}
              state={playerState}
              fileType={fileType}
              imageState={imageState}
              setImageState={setImageState}
              setAction={setAction}
              rowMode={homeItem.row_mode}
              onNext={async () => await UgcIndexSetter(mainUgcIndex, "next")}
            />
          </div>
        )}

        {isMobile && (
          <div className={styles.mobileController}>
            <UgcPlayerControllerMobile
              ugc={mainUgc}
              state={playerState}
              fileType={fileType}
              imageState={imageState}
              setImageState={setImageState}
              setAction={setAction}
              rowMode={homeItem.row_mode}
              onNext={async () => await UgcIndexSetter(mainUgcIndex, "next")}
              onPrevious={async () =>
                await UgcIndexSetter(mainUgcIndex, "previous")
              }
            />
          </div>
        )}
      </UgcModal>
    </>
  );
};

export default FunUGCModal;
